// AdminStructuresPage.jsx - Gestion des structures juridictionnelles
import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from '../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Building2, 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  MapPin,
  Phone,
  Mail,
  GitBranch,
  Shield
} from 'lucide-react';
import api from '../lib/api';

const DRC_PROVINCES = [
  "KINSHASA", "KONGO-CENTRAL", "KWANGO", "KWILU", "MAI-NDOMBE",
  "KASAI", "KASAI-CENTRAL", "KASAI-ORIENTAL", "LOMAMI", "SANKURU",
  "MANIEMA", "SUD-KIVU", "NORD-KIVU", "ITURI", "HAUT-UELE",
  "TSHOPO", "BAS-UELE", "NORD-UBANGI", "SUD-UBANGI", "MONGALA",
  "EQUATEUR", "TSHUAPA", "TANGANYIKA", "HAUT-LOMAMI", "LUALABA", "HAUT-KATANGA"
];

const AdminStructuresPage = () => {
  const [structures, setStructures] = useState([]);
  const [structureTypes, setStructureTypes] = useState({ provincial: [], national: [] });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterProvince, setFilterProvince] = useState('all');
  const [filterJusticeType, setFilterJusticeType] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    structure_type: '',
    level: 'PROVINCIAL',
    province: '',
    parent_structure_id: '',
    has_cachot: false,
    justice_type: 'CIVIL', // NEW: CIVIL or MILITAIRE
    address: '',
    phone: '',
    email: '',
    latitude: null,
    longitude: null,
    gps_address: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [structuresRes, typesRes] = await Promise.all([
        api.get('/structures'),
        api.get('/structure-types')
      ]);
      setStructures(structuresRes.data || []);
      setStructureTypes(typesRes.data || { provincial: [], national: [] });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStructure) {
        await api.put(`/structures/${editingStructure.structure_id}`, formData);
        toast.success('Structure mise à jour avec succès');
      } else {
        await api.post('/structures', formData);
        toast.success('Structure créée avec succès');
      }
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (structureId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette structure?')) return;
    
    try {
      await api.delete(`/structures/${structureId}`);
      toast.success('Structure supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (structure) => {
    setEditingStructure(structure);
    setFormData({
      name: structure.name,
      structure_type: structure.structure_type,
      level: structure.level,
      province: structure.province || '',
      parent_structure_id: structure.parent_structure_id || '',
      has_cachot: structure.has_cachot || false,
      justice_type: structure.justice_type || 'CIVIL',
      address: structure.address || '',
      phone: structure.phone || '',
      email: structure.email || '',
      latitude: structure.latitude || null,
      longitude: structure.longitude || null,
      gps_address: structure.gps_address || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingStructure(null);
    setFormData({
      name: '',
      structure_type: '',
      level: 'PROVINCIAL',
      province: '',
      parent_structure_id: '',
      has_cachot: false,
      justice_type: 'CIVIL',
      address: '',
      phone: '',
      email: '',
      latitude: null,
      longitude: null,
      gps_address: ''
    });
  };

  // Fonction pour obtenir la position GPS actuelle
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
          toast.success('Position GPS obtenue');
        },
        (error) => {
          toast.error('Impossible d\'obtenir la position GPS');
        }
      );
    } else {
      toast.error('La géolocalisation n\'est pas supportée');
    }
  };

  // Ouvrir Google Maps pour sélectionner un emplacement
  const openMapSelector = () => {
    const lat = formData.latitude || -4.3217;
    const lng = formData.longitude || 15.3125;
    window.open(`https://www.google.com/maps?q=${lat},${lng}&z=15`, '_blank');
  };

  const filteredStructures = structures.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.structure_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = filterLevel === 'all' || s.level === filterLevel;
    const matchesProvince = filterProvince === 'all' || s.province === filterProvince;
    const matchesJusticeType = filterJusticeType === 'all' || s.justice_type === filterJusticeType;
    return matchesSearch && matchesLevel && matchesProvince && matchesJusticeType;
  });

  const getParentOptions = () => {
    return structures.filter(s => s.structure_id !== editingStructure?.structure_id);
  };

  const allStructureTypes = [...structureTypes.provincial, ...structureTypes.national];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-structures-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Structures Juridictionnelles
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérer les commissariats, tribunaux, parquets et autres structures
            </p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-structure-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Structure
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-structures"
                />
              </div>
              <Select value={filterJusticeType} onValueChange={setFilterJusticeType}>
                <SelectTrigger data-testid="filter-justice-type">
                  <SelectValue placeholder="Type de Justice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="CIVIL">Justice Civile</SelectItem>
                  <SelectItem value="MILITAIRE">Justice Militaire</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger data-testid="filter-level">
                  <SelectValue placeholder="Niveau" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les niveaux</SelectItem>
                  <SelectItem value="PROVINCIAL">Provincial</SelectItem>
                  <SelectItem value="NATIONAL">National</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterProvince} onValueChange={setFilterProvince}>
                <SelectTrigger data-testid="filter-province">
                  <SelectValue placeholder="Province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les provinces</SelectItem>
                  {DRC_PROVINCES.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-sm text-muted-foreground flex items-center">
                {filteredStructures.length} structure(s) trouvée(s)
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Structures List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStructures.map((structure) => (
              <Card key={structure.structure_id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{structure.name}</CardTitle>
                      <CardDescription className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge variant={structure.level === 'NATIONAL' ? 'default' : 'secondary'}>
                          {structure.level}
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className={structure.justice_type === 'MILITAIRE' 
                            ? 'text-red-600 border-red-600 bg-red-50' 
                            : 'text-blue-600 border-blue-600 bg-blue-50'}
                        >
                          {structure.justice_type === 'MILITAIRE' ? '⚔️ Militaire' : '⚖️ Civil'}
                        </Badge>
                        {structure.has_cachot && (
                          <Badge variant="outline" className="text-orange-600 border-orange-600">
                            <Shield className="w-3 h-3 mr-1" />
                            Cachot
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(structure)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(structure.structure_id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      {allStructureTypes.find(t => t.code === structure.structure_type)?.name || structure.structure_type}
                    </div>
                    {structure.province && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        {structure.province}
                      </div>
                    )}
                    {structure.address && (
                      <div className="flex items-center gap-2 text-muted-foreground truncate">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        {structure.address}
                      </div>
                    )}
                    {structure.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        {structure.phone}
                      </div>
                    )}
                    {structure.parent_structure_id && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <GitBranch className="w-4 h-4" />
                        Parent: {structures.find(s => s.structure_id === structure.parent_structure_id)?.name || 'N/A'}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredStructures.length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                Aucune structure trouvée
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingStructure ? 'Modifier la structure' : 'Nouvelle structure juridictionnelle'}
              </DialogTitle>
              <DialogDescription>
                Remplissez les informations de la structure
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                <div className="md:col-span-2">
                  <Label htmlFor="name">Nom de la structure *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Commissariat Central de Kinshasa"
                    required
                    data-testid="structure-name-input"
                  />
                </div>

                {/* NEW: Justice Type */}
                <div>
                  <Label htmlFor="justice_type">Type de Justice *</Label>
                  <Select 
                    value={formData.justice_type} 
                    onValueChange={(v) => setFormData({...formData, justice_type: v})}
                  >
                    <SelectTrigger data-testid="structure-justice-type-select">
                      <SelectValue placeholder="Civil ou Militaire" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CIVIL">
                        <span className="flex items-center gap-2">
                          ⚖️ Justice Civile
                        </span>
                      </SelectItem>
                      <SelectItem value="MILITAIRE">
                        <span className="flex items-center gap-2">
                          ⚔️ Justice Militaire
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="level">Niveau *</Label>
                  <Select 
                    value={formData.level} 
                    onValueChange={(v) => setFormData({...formData, level: v, structure_type: ''})}
                  >
                    <SelectTrigger data-testid="structure-level-select">
                      <SelectValue placeholder="Sélectionner le niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PROVINCIAL">Provincial</SelectItem>
                      <SelectItem value="NATIONAL">National</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="structure_type">Type de structure *</Label>
                  <Select 
                    value={formData.structure_type} 
                    onValueChange={(v) => {
                      const typeInfo = allStructureTypes.find(t => t.code === v);
                      setFormData({
                        ...formData, 
                        structure_type: v,
                        has_cachot: typeInfo?.has_cachot || false
                      });
                    }}
                  >
                    <SelectTrigger data-testid="structure-type-select">
                      <SelectValue placeholder="Sélectionner le type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(formData.level === 'NATIONAL' ? structureTypes.national : structureTypes.provincial).map(type => (
                        <SelectItem key={type.code} value={type.code}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.level === 'PROVINCIAL' && (
                  <div>
                    <Label htmlFor="province">Province *</Label>
                    <Select 
                      value={formData.province} 
                      onValueChange={(v) => setFormData({...formData, province: v})}
                    >
                      <SelectTrigger data-testid="structure-province-select">
                        <SelectValue placeholder="Sélectionner la province" />
                      </SelectTrigger>
                      <SelectContent>
                        {DRC_PROVINCES.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="parent">Structure parente (hiérarchie)</Label>
                  <Select 
                    value={formData.parent_structure_id} 
                    onValueChange={(v) => setFormData({...formData, parent_structure_id: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Aucune (structure racine)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {getParentOptions().map(s => (
                        <SelectItem key={s.structure_id} value={s.structure_id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Adresse complète"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+243..."
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="contact@structure.gov.cd"
                  />
                </div>

                <div className="md:col-span-2 flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Switch
                    id="has_cachot"
                    checked={formData.has_cachot}
                    onCheckedChange={(v) => setFormData({...formData, has_cachot: v})}
                  />
                  <div>
                    <Label htmlFor="has_cachot" className="cursor-pointer">
                      Cette structure dispose d&apos;un cachot/cellule de détention
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Active cette option si la structure peut héberger des détenus
                    </p>
                  </div>
                </div>

                {/* Section GPS */}
                <div className="md:col-span-2 border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      <Label className="text-base font-medium">Localisation GPS</Label>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={getCurrentLocation}
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        Ma position
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={openMapSelector}
                      >
                        Ouvrir la carte
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="latitude">Latitude</Label>
                      <Input
                        id="latitude"
                        type="number"
                        step="any"
                        value={formData.latitude || ''}
                        onChange={(e) => setFormData({...formData, latitude: parseFloat(e.target.value) || null})}
                        placeholder="Ex: -4.3217"
                      />
                    </div>
                    <div>
                      <Label htmlFor="longitude">Longitude</Label>
                      <Input
                        id="longitude"
                        type="number"
                        step="any"
                        value={formData.longitude || ''}
                        onChange={(e) => setFormData({...formData, longitude: parseFloat(e.target.value) || null})}
                        placeholder="Ex: 15.3125"
                      />
                    </div>
                  </div>
                  
                  {formData.latitude && formData.longitude && (
                    <div className="text-sm text-muted-foreground">
                      <a 
                        href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        Voir sur Google Maps
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="save-structure-btn">
                  {editingStructure ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminStructuresPage;
